import { notifications } from '@markku/db'
import { createInsertSchema, createSelectSchema } from 'drizzle-zod'
import { z } from 'zod'

export const selectNotificationSchema = createSelectSchema(notifications)
export const insertNotificationSchema = createInsertSchema(notifications).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
})

export type Notification = z.infer<typeof selectNotificationSchema>
export type InsertNotification = z.infer<typeof insertNotificationSchema>

// Notification types enum (can expand as needed)
export const NOTIFICATION_TYPES = {
  VISIT_FEEDBACK_REQUEST: 'visit_feedback_request',
  CHAT_MESSAGE: 'chat_message',
  VISIT_REMINDER: 'visit_reminder',
  INVOICE_EMAIL: 'invoice_email',
  BOOKING_CONFIRMATION: 'booking_confirmation',
  CHIMNEY_SWEEP_REMINDER: 'chimney_sweep_reminder',
  CHIMNEY_SWEEP_REMINDER_2: 'chimney_sweep_reminder_2',
  COMMISSION_CONGRATULATIONS: 'commission_congratulations',
  NEWSLETTER: 'newsletter',
  INVOICE_PAYMENT_REMINDER_1: 'invoice_payment_reminder_1',
  INVOICE_PAYMENT_REMINDER_2: 'invoice_payment_reminder_2',
  INVOICE_PAYMENT_REMINDER_3: 'invoice_payment_reminder_3',
  ABSENCE_REPORTED: 'absence_reported',
  PRE_INFO_REMINDER: 'pre_info_reminder',
  VISIT_TIME_CONFIRMED: 'visit_time_confirmed',
  SERVICE_RECOMMENDATION: 'service_recommendation',
  SERVICE_RECOMMENDATION_REMINDER: 'service_recommendation_reminder',
  THANK_YOU_VOUCHER: 'thank_you_voucher',
  THANK_YOU_VOUCHER_REMINDER: 'thank_you_voucher_reminder',
  TASK_ASSIGNED: 'task_assigned',
  OFFER_UNOPENED_REMINDER: 'offer_unopened_reminder',
} as const

export type NotificationType =
  (typeof NOTIFICATION_TYPES)[keyof typeof NOTIFICATION_TYPES]

/** Maps notification types to the table their entityId references */
export const NOTIFICATION_ENTITY_TABLE = {
  visit_feedback_request: 'visits',
  visit_reminder: 'visits',
  booking_confirmation: 'visits',
  chimney_sweep_reminder: 'visits',
  chimney_sweep_reminder_2: 'visits',
  commission_congratulations: 'visits',
  pre_info_reminder: 'visits',
  visit_time_confirmed: 'visits',
  invoice_email: 'invoices',
  invoice_payment_reminder_1: 'invoices',
  invoice_payment_reminder_2: 'invoices',
  invoice_payment_reminder_3: 'invoices',
  newsletter: 'newsletters',
  chat_message: 'chat_messages',
  absence_reported: 'absences',
  service_recommendation: 'visit_recommendations',
  service_recommendation_reminder: 'visit_recommendations',
  thank_you_voucher: 'visits',
  thank_you_voucher_reminder: 'visits',
  task_assigned: 'tasks',
  offer_unopened_reminder: 'offers',
} as const satisfies Record<NotificationType, string>

// Marketing mail: sent from the newsletter identity with one-click
// unsubscribe headers, and never sent to an unsubscribed address.
export const MARKETING_NOTIFICATION_TYPES: NotificationType[] = [
  NOTIFICATION_TYPES.NEWSLETTER,
  NOTIFICATION_TYPES.SERVICE_RECOMMENDATION,
  NOTIFICATION_TYPES.SERVICE_RECOMMENDATION_REMINDER,
  NOTIFICATION_TYPES.THANK_YOU_VOUCHER,
  NOTIFICATION_TYPES.THANK_YOU_VOUCHER_REMINDER,
]

export const VISIT_NOTIFICATION_TYPES = Object.entries(
  NOTIFICATION_ENTITY_TABLE,
)
  .filter(([, table]) => table === 'visits')
  .map(([type]) => type) as NotificationType[]

export const INVOICE_NOTIFICATION_TYPES = Object.entries(
  NOTIFICATION_ENTITY_TABLE,
)
  .filter(([, table]) => table === 'invoices')
  .map(([type]) => type) as NotificationType[]

/** All notification types whose entityId references a customer-owned entity (visit or invoice) */
export const ENTITY_NOTIFICATION_TYPES: NotificationType[] = [
  ...VISIT_NOTIFICATION_TYPES,
  ...INVOICE_NOTIFICATION_TYPES,
]

// Notification channels
export const NOTIFICATION_CHANNELS = {
  EMAIL: 'email',
  PUSH: 'push',
  SMS: 'sms',
} as const

export type NotificationChannel =
  (typeof NOTIFICATION_CHANNELS)[keyof typeof NOTIFICATION_CHANNELS]

// Notification status
export const NOTIFICATION_STATUS = {
  PENDING: 'pending',
  PROCESSING: 'processing',
  SENT: 'sent',
  SKIPPED: 'skipped',
  FAILED: 'failed',
  BOUNCED: 'bounced',
} as const

export type NotificationStatus =
  (typeof NOTIFICATION_STATUS)[keyof typeof NOTIFICATION_STATUS]
